import BoltIcon from "@mui/icons-material/Bolt";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  blue,
  deepOrange,
  green,
  indigo,
  lime,
  orange,
  pink,
  purple,
} from "@mui/material/colors";
import LoanAmountSimulationForm from "../components/form/LoanAmountSimulationForm";
import PaymentFlexibilitySimulationForm from "../components/form/PaymentFlexibilitySimulationForm";
import TermLengthSimulationForm from "../components/form/TermLengthSimulationForm";
import InterestRateSimulationForm from "../components/form/InterestRateSimulationForm";
import MonthlyPaymentSimulationForm from "../components/form/MonthlyPaymentSimulationForm";
import AcrSimulationForm from "../components/form/AcrSimulationForm";
import SimulationForm from "../components/form/SimulationForm";
import AcrPrimaryMetric from "../components/metrics/AcrPrimaryMetric";
import AcrSecondaryMetric from "../components/metrics/AcrSecondaryMetric";
import LoanAmountInsights from "../components/mode-insights/LoanAmountInsights";
import DefaultPrimaryMetric from "../components/metrics/DefaultPrimaryMetric";
import DefaultSecondaryMetric from "../components/metrics/DefaultSecondaryMetric";
import TermLengthPrimaryMetric from "../components/metrics/TermLengthPrimaryMetric";
import TermLengthSecondaryMetric from "../components/metrics/TermLengthSecondaryMetric";
import InterestRatePrimaryMetric from "../components/metrics/InterestRatePrimaryMetric";
import InterestRateSecondaryMetric from "../components/metrics/InterestRateSecondaryMetric";
import MonthlyPaymentPrimaryMetric from "../components/metrics/MonthlyPaymentPrimaryMetric";
import MonthlyPaymentSecondaryMetric from "../components/metrics/MonthlyPaymentSecondaryMetric";
import MonthlyPaymentInsights from "../components/mode-insights/MonthlyPaymentInsights";
import TermLengthInsights from "../components/mode-insights/TermLengthInsights";
import InterestRateInsights from "../components/mode-insights/InterestRateInsights";
import AcrInsights from "../components/mode-insights/AcrInsights";
import PaymentFlexibilityInsights from "../components/mode-insights/PaymentFlexibilityInsights";
import { SimulationFormProps } from "../types/forms";
import { ModeInsightsProps } from "../types/insights";
import { MetricComponentProps } from "../types/metrics";

export type ModeKey =
  | "manual"
  | "singleSimulation"
  | "loanAmount"
  | "termLength"
  | "interestRate"
  | "monthlyPayment"
  | "additionalCapitalRepayments"
  | "paymentFlexibility";

export interface ModeDefinition<FormProps = any, ModeInsightsProps = any> {
  label: string;
  icon: React.ComponentType<any> | null;
  description: string;
  color1?: string;
  color2?: string;
  components: {
    Form?: React.ComponentType<FormProps>;
    ModeInsights?: React.ComponentType<ModeInsightsProps>;
  };
  metrics?: {
    Primary?: React.ComponentType<MetricComponentProps>;
    Secondary?: React.ComponentType<MetricComponentProps>;
  };
  canEdit?: boolean; // default true
  canDuplicate?: boolean; // default true
  canDelete?: boolean; // default true
  minimumSimulations?: number; // minimum number of simulations required (default 1)
  hideSecondaryMetricInCard?: boolean; // default false - hides secondary metric in card view but keeps it in simulation page
  controlledSettings?: {
    equalizeLength?: boolean;
  };
}

export const modes: Record<
  ModeKey,
  ModeDefinition<SimulationFormProps, ModeInsightsProps>
> = {
  singleSimulation: {
    label: "Single Simulation",
    icon: BoltIcon,
    description:
      "Create a single simulation and view detailed breakdowns of payments and costs over time.",
    color1: "#7CAA6D",
    color2: "#22D4A0",
    components: {
      Form: SimulationForm,
    },
    metrics: {
      Primary: DefaultPrimaryMetric,
      Secondary: DefaultSecondaryMetric,
    },
    canDuplicate: false,
    canDelete: false,
  },
  loanAmount: {
    label: "Loan Amounts",
    icon: null,
    description: "Compare the cost of different loan amounts side by side.",
    color1: indigo[800],
    color2: blue[500],
    components: {
      Form: LoanAmountSimulationForm,
      ModeInsights: LoanAmountInsights,
    },
    metrics: {
      Primary: DefaultPrimaryMetric,
      Secondary: DefaultSecondaryMetric,
    },
    minimumSimulations: 2,
    controlledSettings: {
      equalizeLength: true,
    },
  },
  termLength: {
    label: "Term Lengths",
    icon: null,
    description: "Compare the cost of different term lengths side by side.",
    color1: deepOrange[900],
    color2: orange[600],
    components: {
      Form: TermLengthSimulationForm,
      ModeInsights: TermLengthInsights,
    },
    metrics: {
      Primary: TermLengthPrimaryMetric,
      Secondary: TermLengthSecondaryMetric,
    },
    minimumSimulations: 2,
    controlledSettings: {
      equalizeLength: true,
    },
  },
  interestRate: {
    label: "Interest Rates",
    icon: null,
    description: "Compare the cost of different interest rates side by side.",
    color1: green[600],
    color2: lime[500],
    components: {
      Form: InterestRateSimulationForm,
      ModeInsights: InterestRateInsights,
    },
    metrics: {
      Primary: InterestRatePrimaryMetric,
      Secondary: InterestRateSecondaryMetric,
    },
    minimumSimulations: 2,
    controlledSettings: {
      equalizeLength: true,
    },
  },
  monthlyPayment: {
    label: "Monthly Payments",
    icon: null,
    description: "Compare the cost of different monthly payments side by side.",
    color1: purple[400],
    color2: pink[300],
    components: {
      Form: MonthlyPaymentSimulationForm,
      ModeInsights: MonthlyPaymentInsights,
    },
    metrics: {
      Primary: MonthlyPaymentPrimaryMetric,
      Secondary: MonthlyPaymentSecondaryMetric,
    },
    minimumSimulations: 2,
    controlledSettings: {
      equalizeLength: true,
    },
  },
  additionalCapitalRepayments: {
    label: "Additional Capital Repayments",
    icon: null,
    description:
      "Compare the cost of different additional capital repayment amounts side by side.",
    color1: "#9055FF",
    color2: "#13E2DA",
    components: {
      Form: AcrSimulationForm,
      ModeInsights: AcrInsights,
    },
    metrics: {
      Primary: AcrPrimaryMetric,
      Secondary: AcrSecondaryMetric,
    },
    minimumSimulations: 2,
    hideSecondaryMetricInCard: true,
    controlledSettings: {
      equalizeLength: true,
    },
  },
  paymentFlexibility: {
    label: "Monthly Payment Flexibility",
    icon: null,
    description:
      "Compare term lengths to understand the cost of flexibility. Shortest term sets the budget; longer terms use the surplus for additional capital repayments.",
    color1: "#4b6cb7",
    color2: "#182848",
    components: {
      Form: PaymentFlexibilitySimulationForm,
      ModeInsights: PaymentFlexibilityInsights,
    },
    metrics: {
      Primary: TermLengthPrimaryMetric,
      Secondary: TermLengthSecondaryMetric,
    },
    minimumSimulations: 2,
    canDuplicate: false,
    canDelete: false,
    controlledSettings: {
      equalizeLength: true,
    },
  },
  manual: {
    label: "Manual Mode",
    icon: SettingsIcon,
    description:
      "Add individual simulations with full control over parameters, but fewer comparison insights.",
    components: {
      Form: SimulationForm,
    },
    metrics: {
      Primary: DefaultPrimaryMetric,
      Secondary: DefaultSecondaryMetric,
    },
  },
};
